import { useState } from 'react';
import { css } from '@emotion/react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { ScopedSingleLocationFilterMenu } from '@frontend/location-filter-menu';
import { useFormField } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { useScheduleActivityFilters } from './filters/use-schedule-activity-filters';
import { ScheduleActivityTable } from './schedule-activity-table';
import { useMockScheduleActivity } from './use-mock-schedule-activity';

export type PageConfig = { page: number; limit: number };

export const ScheduleActivity = () => {
  const [pageConfig, setPageConfig] = useState<PageConfig>({ page: 1, limit: 50 });

  const { getOnlineSchedulingReadyLocationIds } = useSchedulingLocationInfo();
  const onlineSchedulingReadyLocationIds = getOnlineSchedulingReadyLocationIds();

  const { isIntegratedOffice } = DataSourcesQueries.useGetAccessibleIntegrations();
  const showServiceColumn = isIntegratedOffice;

  // Location dropdown form field
  const selectedLocationField = useFormField(
    {
      type: 'checklist',
      value: onlineSchedulingReadyLocationIds.length ? [onlineSchedulingReadyLocationIds[0]] : [],
    },
    [onlineSchedulingReadyLocationIds]
  );
  const selectedLocationId = selectedLocationField.value[0] ?? '';

  const { data, isLoading, refetch } = SchedulerV3.Queries.useListScheduleActivityQuery({
    request: {
      locationId: selectedLocationId,
      limit: pageConfig.limit,
      page: pageConfig.page,
    },
    options: {
      enabled: !!selectedLocationId,
    },
  });

  const { data: mockData } = useMockScheduleActivity();
  const locationScopedData = (mockData ?? []).filter((row) => row.locationId === selectedLocationId);

  // TODO: Use the actual filters from the API once available
  const { filters, filterOptions, setFilters } = useScheduleActivityFilters(selectedLocationId, locationScopedData);

  return (
    <ScheduleActivityTable
      data={data?.items ?? []}
      totalItems={data?.totalCount ?? 0}
      pageConfig={pageConfig}
      setPageConfig={setPageConfig}
      filterOptions={filterOptions}
      filters={filters}
      setFilters={setFilters}
      isLoading={isLoading}
      showServiceColumn={showServiceColumn}
      locationId={selectedLocationId}
      locationDropdown={
        onlineSchedulingReadyLocationIds.length > 1 && (
          <div css={{ marginRight: 'auto' }}>
            <ScopedSingleLocationFilterMenu
              field={selectedLocationField}
              locationIds={onlineSchedulingReadyLocationIds}
              css={locationDropdownStyles}
            />
          </div>
        )
      }
      onRefetch={refetch}
    />
  );
};

const locationDropdownStyles = css`
  background-color: var(--fabric-button-primary-background-default);
  color: var(--fabric-color-neutral-00);
  p {
    color: var(--fabric-color-neutral-00);
  }
`;
